import { useCallback, useEffect, useRef, useState } from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import PenIcon from '@cartwhl/ui/icons/PenIcon';

type EditableCharacterFileNameProps = {
  fileName: string;
  /** Whether the filename can be edited from the header. */
  editable: boolean;
  disabled?: boolean;
  onRename: (nextDisplayFileName: string) => Promise<boolean>;
};

export default function EditableCharacterFileName({
  fileName,
  editable,
  disabled = false,
  onRename,
}: EditableCharacterFileNameProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(fileName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(fileName);
  }, [fileName]);

  const beginEdit = useCallback(() => {
    if (!editable || disabled || isEditing) return;
    setIsEditing(true);
  }, [editable, disabled, isEditing]);

  const commit = useCallback(async () => {
    setIsEditing(false);
    const next = draft.trim();
    if (!next) {
      setDraft(fileName);
      return;
    }
    if (next === fileName) return;
    const ok = await onRename(next);
    if (!ok) setDraft(fileName);
  }, [draft, fileName, onRename]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void commit();
      }
      if (e.key === 'Escape') {
        setDraft(fileName);
        setIsEditing(false);
      }
    },
    [commit, fileName],
  );

  useEffect(() => {
    if (!isEditing || !inputRef.current) return;
    const input = inputRef.current;
    input.focus();
    input.setSelectionRange(0, input.value.length);
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing || !inputRef.current) return;
    const input = inputRef.current;
    const measure = document.createElement('span');
    measure.style.visibility = 'hidden';
    measure.style.position = 'absolute';
    measure.style.whiteSpace = 'pre';
    measure.style.font = window.getComputedStyle(input).font;
    measure.textContent = input.value || input.placeholder || 'M';
    document.body.appendChild(measure);
    const width = measure.offsetWidth;
    document.body.removeChild(measure);
    input.style.width = `${Math.max(width + 10, 48)}px`;
  }, [isEditing, draft]);

  if (!editable) {
    return (
      <Text variant="studio/title/md" className="truncate text-center">
        {fileName}
      </Text>
    );
  }

  if (isEditing) {
    return (
      <div className="flex min-w-0 flex-1 justify-center overflow-x-auto">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={onKeyDown}
          className="max-w-full border-none bg-transparent text-center font-sans text-[16px]/[20px] font-normal outline-hidden"
          style={{ width: 'max-content', minWidth: '3rem' }}
          aria-label="Character file name"
        />
      </div>
    );
  }

  return (
    <Tooltip text="Rename character" side="bottom" inModal>
      <button
        type="button"
        disabled={disabled}
        onClick={beginEdit}
        className="group inline-flex max-w-full min-w-0 cursor-pointer items-center justify-center gap-1 disabled:opacity-50"
      >
        <Text
          variant="studio/title/md"
          className="min-w-0 truncate text-center"
        >
          {fileName}
        </Text>
        <PenIcon className="text-gray-60 group-hover:border-gray-30 dark:group-hover:border-gray-70 hover:bg-gray-30 dark:hover:bg-gray-70 h-6 w-6 shrink-0 rounded-lg border border-transparent p-0.5 transition-colors" />
      </button>
    </Tooltip>
  );
}
